import React, { useCallback, useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "../lib/api";

const TIMEOUT_MS = 8000;
const MAX_ATTEMPTS = 8;

async function probeBackend() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/health?t=${Date.now()}`,
      { cache: "no-store", signal: controller.signal }
    );
    if (!res.ok) return false;
    const data = await res.json();
    return data && data.status === "ok";
  } catch (err) {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export default function BootGate({ children }) {
  const [phase, setPhase] = useState("checking");
  const [attempt, setAttempt] = useState(0);
  const cancelled = useRef(false);

  const run = useCallback(async () => {
    cancelled.current = false;
    for (let i = 1; i <= MAX_ATTEMPTS; i += 1) {
      if (cancelled.current) return;
      setAttempt(i);
      if (i > 1) setPhase("waking");
      const alive = await probeBackend();
      if (cancelled.current) return;
      if (alive) {
        setPhase("ready");
        return;
      }
      const delay = Math.min(1000 * i, 5000);
      await new Promise((r) => setTimeout(r, delay));
    }
    if (!cancelled.current) setPhase("down");
  }, []);

  useEffect(() => {
    run();
    return () => {
      cancelled.current = true;
    };
  }, [run]);

  if (phase === "ready") return children;

  const wrap = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0B0D10",
    color: "#E7E9EC",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    padding: "24px",
  };
  const card = { maxWidth: "420px", textAlign: "center" };
  const title = {
    fontSize: "20px",
    fontWeight: 600,
    margin: "0 0 12px",
    letterSpacing: "-0.01em",
  };
  const body = {
    fontSize: "15px",
    lineHeight: 1.6,
    color: "#9AA3AE",
    margin: "0 0 20px",
  };
  const button = {
    background: "#E7E9EC",
    color: "#0B0D10",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
  };
  const dot = {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: "#4C8DFF",
    display: "inline-block",
    marginRight: "8px",
    animation: "bootPulse 1.2s ease-in-out infinite",
  };

  return (
    <div style={wrap} data-testid="boot-gate">
      <style>
        {"@keyframes bootPulse{0%,100%{opacity:.3}50%{opacity:1}}"}
      </style>
      <div style={card}>
        {phase === "down" ? (
          <>
            <h1 style={title} data-testid="boot-gate-down-title">El servicio no responde</h1>
            <p style={body}>
              No pudimos conectar con el servidor de Stratika. Ningún dato
              mostrado sería confiable, así que preferimos no mostrar nada.
            </p>
            <button
              style={button}
              data-testid="boot-gate-retry-btn"
              onClick={() => {
                setPhase("checking");
                setAttempt(0);
                run();
              }}
            >
              Reintentar
            </button>
          </>
        ) : (
          <>
            <h1 style={title} data-testid="boot-gate-checking-title">
              <span style={dot} />
              Iniciando el servicio
            </h1>
            <p style={body}>
              El servidor está despertando. Esto toma unos segundos.
              Intento {attempt} de {MAX_ATTEMPTS}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
